import { Md5 } from 'ts-md5'
import BaseService from '../../BaseService'
import type { CustomAxiosResponse, PageParams } from '../../types'
import type {
  ChargeCommodity,
  ChargeOrder,
  CreateChargeOrderOptions,
  CreateWithdrawOrderOptions,
  GetChargeCommodityListOptions,
  GetChargeOrderListOptions,
  GetPaymentsOptions,
  GetTradeRecordListOptions,
  GetWithdrawOrderListOptions,
  Payment,
  PayOptions,
  PayOrder,
  TradeRecord,
  TrandeRecordSummary,
  UpdateUserWalletConfigOptions,
  UpdateUserWalletPasswordOptions,
  UserWallet,
  UserWalletConfig,
  WithdrawOrder
} from '../../types/wallet'

class WalletServer extends BaseService {
  /**
   * 获取用户钱包信息
   * @returns 用户钱包信息
   */
  public async getUserWallet(): Promise<CustomAxiosResponse<UserWallet>> {
    return this.request.get<unknown, CustomAxiosResponse<UserWallet>>(`${this.apiUrl}/v1/pay/app/accounts`)
  }

  /**
   * 获取用户钱包配置信息
   * @returns 用户钱包配置信息
   */
  public async getUserWalletConfig(): Promise<CustomAxiosResponse<UserWalletConfig>> {
    return this.request.get<unknown, CustomAxiosResponse<UserWalletConfig>>(`${this.apiUrl}/v1/pay/app/account-config`)
  }

  /**
   * 更新用户钱包配置信息
   * @param options 参数
   * @param options.accountName 账号名称
   * @param options.realName 真实姓名
   * @param options.extInfo 扩展信息
   * @param options.type 账户类型
   * @param options.validLoginPassword 有效登录密码
   * @returns null
   */
  public async updateUserWalletConfig(options: UpdateUserWalletConfigOptions): Promise<CustomAxiosResponse<null>> {
    const { validLoginPassword, ...rest } = options
    return this.request.put<UserWalletConfig, CustomAxiosResponse<null>>(`${this.apiUrl}/v1/pay/app/account-config`, {
      ...rest,
      validLoginPassword: Md5.hashStr(validLoginPassword)
    })
  }

  /**
   * 更新用户钱包密码
   * @param options 参数
   * @param options.password 密码
   * @param options.validLoginPassword 有效登录密码
   * @returns null
   */
  public async updateUserWalletPassword(options: UpdateUserWalletPasswordOptions): Promise<CustomAxiosResponse<null>> {
    const { password, validLoginPassword } = options
    return this.request.put<unknown, CustomAxiosResponse<null>>(`${this.apiUrl}/v1/pay/app/account-config/password`, {
      password: Md5.hashStr(password),
      validLoginPassword: Md5.hashStr(validLoginPassword)
    })
  }

  /**
   * 获取充值商品列表
   * @param options 参数
   * @param options.currencyCode 充值币种
   * @param options.paymentCurrencyCode 支付币种
   * @param options.price 交易价格
   * @param options.quantity 数量
   * @param options.pageNum 页码
   * @param options.pageSize 页大小
   * @param options.orderBy 排序字段
   * @returns 商品列表
   */
  public async getChargeCommodityList(
    options: GetChargeCommodityListOptions & PageParams
  ): Promise<CustomAxiosResponse<ChargeCommodity[]>> {
    return this.request.get<unknown, CustomAxiosResponse<ChargeCommodity[]>>(`${this.apiUrl}/v1/pay/app/commodity`, {
      params: {
        ...options
      }
    })
  }

  /**
   * 获取充值商品详情
   * @param id 商品ID
   * @returns 商品详情
   */
  public async getChargeCommodityDetail(id: number): Promise<CustomAxiosResponse<ChargeCommodity>> {
    return this.request.get<unknown, CustomAxiosResponse<ChargeCommodity>>(`${this.apiUrl}/v1/pay/app/commodity/${id}`)
  }

  /**
   * 获取支付渠道
   * @param options 参数
   * @param options.currencyCode 币种编码
   * @param options.platformCode 平台编码
   * @returns 支付渠道列表
   */
  public async getPayments(options: GetPaymentsOptions): Promise<CustomAxiosResponse<Payment[]>> {
    return this.request.get<unknown, CustomAxiosResponse<Payment[]>>(`${this.apiUrl}/v1/pay/app/payment/channel`, {
      params: { ...options }
    })
  }

  /**
   * 创建充值定单
   * @param options 参数
   * @param options.commodityId 商品ID
   * @param options.autoPay 是否自动支付
   * @param options.quantity 数量
   * @param options.paymentCode 支付渠道编码
   * @param options.redirectUrl 支付成功后跳转地址
   * @returns 充值订单
   */
  public async createChargeOrder(options: CreateChargeOrderOptions): Promise<CustomAxiosResponse<ChargeOrder>> {
    const { autoPay = 0, ...rest } = options
    return this.request.post<unknown, CustomAxiosResponse<ChargeOrder>>(`${this.apiUrl}/v1/pay/app/commodity/orders`, {
      ...rest,
      autoPay
    })
  }

  /**
   * 获取充值订单列表
   * @param options 参数
   * @param options.orderNo 订单号
   * @param options.payOrderNo 支付订单号
   * @param options.commodityName 商品名称
   * @param options.currencyCode 币种编码
   * @param options.tradeStatus 交易状态
   * @param options.createTimeFrom 创建时间开始
   * @param options.createTimeTo 创建时间结束
   * @param options.pageNum 页码
   * @param options.pageSize 页大小
   * @param options.orderBy 排序字段
   * @returns 充值订单列表
   */
  public async getChargeOrderList(
    options: GetChargeOrderListOptions & PageParams = {}
  ): Promise<CustomAxiosResponse<ChargeOrder[]>> {
    return this.request.get<unknown, CustomAxiosResponse<ChargeOrder[]>>(`${this.apiUrl}/v1/pay/app/commodity/orders`, {
      params: {
        ...options
      }
    })
  }

  /**
   * 获取充值订单详情
   * @param orderNo 订单号
   * @returns 充值订单详情
   */
  public async getChargeOrderDetail(orderNo: string): Promise<CustomAxiosResponse<ChargeOrder>> {
    return this.request.get<unknown, CustomAxiosResponse<ChargeOrder>>(
      `${this.apiUrl}/v1/pay/app/commodity/orders/${orderNo}`
    )
  }

  /**
   * 删除充值订单
   * @param orderNo 订单号
   * @returns null
   */
  public async deleteChargeOrder(orderNo: string): Promise<CustomAxiosResponse<null>> {
    return this.request.delete<unknown, CustomAxiosResponse<null>>(
      `${this.apiUrl}/v1/pay/app/commodity/orders/${orderNo}`
    )
  }

  /**
   * 取消充值订单
   * @param orderNo 订单号
   * @returns null
   */
  public async cancelChargeOrder(orderNo: string): Promise<CustomAxiosResponse<null>> {
    return this.request.put<unknown, CustomAxiosResponse<null>>(
      `${this.apiUrl}/v1/pay/app/commodity/orders/close/${orderNo}`
    )
  }

  /**
   * 创建提现订单
   * @param options 参数
   * @param options.amount 金额
   * @param options.amountTo 转账金额
   * @param options.currencyCode 币种编码
   * @param options.currencyCodeTo 转账币种编码
   * @param options.paymentCode 支付渠道编码
   * @param options.password 支付密码
   * @returns 提现订单
   */
  public async createWithdrawOrder(options: CreateWithdrawOrderOptions): Promise<CustomAxiosResponse<WithdrawOrder>> {
    return this.request.post<unknown, CustomAxiosResponse<WithdrawOrder>>(`${this.apiUrl}/v1/pay/app/withdraw/orders`, {
      ...options
    })
  }

  /**
   * 获取提现订单列表
   * @param options 参数
   * @param options.orderNo 订单号
   * @param options.configWithdrawChannelCode 提现渠道编码
   * @param options.currencyCode 源币币种
   * @param options.currencyCodeTo 目标币种代码
   * @param options.tradeStatus 交易状态 -1已驳回 0已提交 1已审核 2已提现
   * @param options.createTimeFrom 创建时间开始
   * @param options.createTimeTo 创建时间结束
   * @param options.pageNum 页码
   * @param options.pageSize 页大小
   * @param options.orderBy 排序字段
   * @returns 提现订单列表
   */
  public async getWithdrawOrderList(
    options: GetWithdrawOrderListOptions & PageParams = {}
  ): Promise<CustomAxiosResponse<WithdrawOrder[]>> {
    return this.request.get<unknown, CustomAxiosResponse<WithdrawOrder[]>>(
      `${this.apiUrl}/v1/pay/app/withdraw/orders`,
      {
        params: {
          ...options
        }
      }
    )
  }

  /**
   * 获取提现订单详情
   * @param orderNo 订单号
   * @returns 提现订单详情
   */
  public async getWithdrawOrderDetail(orderNo: string): Promise<CustomAxiosResponse<WithdrawOrder>> {
    return this.request.get<unknown, CustomAxiosResponse<WithdrawOrder>>(
      `${this.apiUrl}/v1/pay/app/withdraw/orders/${orderNo}`
    )
  }

  /**
   * 获取交易记录列表
   * @param options 参数
     @param options.currencyCode 币种编码
     @param options.businessCode 业务编码
     @param options.createTimeFrom 开始时间
     @param options.createTimeTo 结束时间
     @param options.type 交易类型: 1-转入 2-转出
     @param options.pageNum 页码
     @param options.pageSize 页大小
     @param options.orderBy 排序字段
   * @returns 交易记录列表
   */
  public async getTradeRecordList(
    options: GetTradeRecordListOptions & PageParams
  ): Promise<CustomAxiosResponse<TradeRecord[]>> {
    return this.request.get<unknown, CustomAxiosResponse<TradeRecord[]>>(
      `${this.apiUrl}/v1/pay/app/account/trade/record`,
      {
        params: {
          ...options
        }
      }
    )
  }

  /**
   * 获取交易记录详情
   * @param id 记录ID
   * @returns 交易记录详情
   */
  public async getTradeRecordDetail(id: number): Promise<CustomAxiosResponse<TradeRecord>> {
    return this.request.get<unknown, CustomAxiosResponse<TradeRecord>>(
      `${this.apiUrl}/v1/pay/app/account/trade/record/${id}`
    )
  }

  /**
   * 获取交易记录汇总
   * @param options 参数
     @param options.currencyCode 币种编码
     @param options.businessCode 业务编码
     @param options.createTimeFrom 开始时间
     @param options.createTimeTo 结束时间
     @param options.type 交易类型: 1-转入 2-转出
   * @returns 交易记录汇总列表
   */
  public async getTrandeRecordSummary(
    options: GetTradeRecordListOptions
  ): Promise<CustomAxiosResponse<TrandeRecordSummary[]>> {
    return this.request.get<unknown, CustomAxiosResponse<TrandeRecordSummary[]>>(
      `${this.apiUrl}/v1/pay/app/account/trade/record/sum`,
      {
        params: {
          ...options
        }
      }
    )
  }

  /**
   * 获取支付订单详情
   * @param payOrderNo 支付订单号
   * @returns 支付订单详情
   */
  public async getPayOrderDetail(payOrderNo: string): Promise<CustomAxiosResponse<PayOrder>> {
    return this.request.get<unknown, CustomAxiosResponse<PayOrder>>(
      `${this.apiUrl}/v1/pay/app/payment/orders/${payOrderNo}`
    )
  }

  /**
   * 支付
   * @param options 参数
   * @param options.payOrderNo 支付订单号
   * @param options.paymentCode 支付渠道编码
   * @param options.extPayOrderNo 外部支付订单号
   * @returns 支付订单详情
   */
  public async pay(options: PayOptions): Promise<CustomAxiosResponse<PayOrder>> {
    return this.request.post<unknown, CustomAxiosResponse<PayOrder>>(
      `${this.apiUrl}/v1/pay/app/payment/orders/confirm`,
      options
    )
  }
}

export default WalletServer
